import { useState, useCallback, useRef, useEffect } from "react";
import { useWebsocket } from "@finitless/shared/contexts";

/**
 * Hook for managing order notifications via websockets
 * Handles real-time order updates and new order notifications
 */
export const useOrderNotifications = () => {
  const { isConnected, registerEventListener, unregisterEventListener } =
    useWebsocket();

  const [notifications, setNotifications] = useState([]);
  const [isEnabled, setIsEnabled] = useState(true);
  const [listenersSetup, setListenersSetup] = useState(false);
  const notificationIdRef = useRef(0);
  const hookId = useRef(`useOrderNotifications-${Date.now()}-${Math.random()}`);
  const isEnabledRef = useRef(isEnabled);

  /**
   * Add a new notification
   */
  const addNotification = useCallback((order, type = "new") => {
    const notificationId = ++notificationIdRef.current;
    const notification = {
      id: notificationId,
      order,
      type,
      timestamp: order?.created_at,
      isVisible: true,
    };

    setNotifications((prev) => [notification, ...prev.slice(0, 4)]); // Keep only last 5 notifications
  }, []);

  /**
   * Remove a notification
   */
  const removeNotification = useCallback((notificationId) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== notificationId),
    );
  }, []);

  /**
   * Clear all notifications
   */
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  /**
   * Handle new order registration
   */
  const handleNewOrder = useCallback(
    (orderData) => {
      if (!isEnabledRef.current) return;

      // Filter by conversation_id (only AI-generated orders)
      if (!orderData.conversation_id) {
        return;
      }

      addNotification(orderData, "new");
    },
    [addNotification],
  );

  /**
   * Handle order updates
   */
  const handleOrderUpdate = useCallback(
    (orderData) => {
      if (!isEnabledRef.current) return;

      // Filter by conversation_id (only AI-generated orders)
      if (!orderData.conversation_id) {
        return;
      }

      // Only show notifications for significant status changes
      const significantUpdates = [
        "time_status",
        "logistic_status",
        "logistic_attemps",
        "locked_at",
        "last_logistic_attempted_at",
      ];

      const hasSignificantUpdate = significantUpdates.some((field) =>
        Object.prototype.hasOwnProperty.call(orderData, field),
      );

      if (hasSignificantUpdate) {
        addNotification(orderData, "update");
      }
    },
    [addNotification],
  );

  /**
   * Handle new messages
   */
  const handleNewMessage = useCallback(() => {
    // You can add message-specific notification logic here if needed
    // For now, we'll just log it
  }, []);

  // Use refs to maintain stable references
  const handleNewOrderRef = useRef(handleNewOrder);
  const handleOrderUpdateRef = useRef(handleOrderUpdate);
  const handleNewMessageRef = useRef(handleNewMessage);

  // Update refs when handlers change
  useEffect(() => {
    handleNewOrderRef.current = handleNewOrder;
    handleOrderUpdateRef.current = handleOrderUpdate;
    handleNewMessageRef.current = handleNewMessage;
  }, [handleNewOrder, handleOrderUpdate, handleNewMessage]);

  useEffect(() => {
    isEnabledRef.current = isEnabled;
  }, [isEnabled]);

  /**
   * Setup socket listeners using centralized event system
   */
  const setupSocketListeners = useCallback(() => {
    if (!isConnected || listenersSetup) return;

    // Use wrapper functions that always delegate to the current ref value
    registerEventListener(
      "orders_register",
      (data) => handleNewOrderRef.current(data),
      hookId.current,
    );
    registerEventListener(
      "update_order",
      (data) => handleOrderUpdateRef.current(data),
      hookId.current,
    );
    registerEventListener(
      "message",
      (data) => handleNewMessageRef.current(data),
      hookId.current,
    );

    setListenersSetup(true);
  }, [isConnected, listenersSetup, registerEventListener]);

  /**
   * Cleanup socket listeners using centralized event system
   */
  const cleanupSocketListeners = useCallback(() => {
    if (!listenersSetup) return;

    // Unregister from centralized event system
    unregisterEventListener("orders_register", hookId.current);
    unregisterEventListener("update_order", hookId.current);
    unregisterEventListener("message", hookId.current);

    setListenersSetup(false);
  }, [listenersSetup, unregisterEventListener]);

  /**
   * Toggle notifications on/off
   */
  const toggleNotifications = useCallback(() => {
    setIsEnabled((prev) => !prev);
  }, []);

  // Setup listeners when connected
  useEffect(() => {
    if (isConnected && !listenersSetup) {
      // Use wrapper functions that always delegate to the current ref value
      registerEventListener(
        "orders_register",
        (data) => handleNewOrderRef.current(data),
        hookId.current,
      );
      registerEventListener(
        "update_order",
        (data) => handleOrderUpdateRef.current(data),
        hookId.current,
      );
      registerEventListener(
        "message",
        (data) => handleNewMessageRef.current(data),
        hookId.current,
      );

      setListenersSetup(true);
    }
  }, [isConnected, registerEventListener]);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      if (listenersSetup) {
        // Unregister from centralized event system
        unregisterEventListener("orders_register", hookId.current);
        unregisterEventListener("update_order", hookId.current);
        unregisterEventListener("message", hookId.current);

        setListenersSetup(false);
      }
    };
  }, [listenersSetup, unregisterEventListener]);

  return {
    notifications,
    isEnabled,
    isConnected,
    addNotification,
    removeNotification,
    clearAllNotifications,
    setupSocketListeners,
    cleanupSocketListeners,
    toggleNotifications,
    handleNewOrder,
    handleOrderUpdate,
    handleNewMessage,
  };
};

export default useOrderNotifications;
